import { useState, useRef, useEffect, useCallback } from 'react';
import { render } from '@react-email/render';
import { AppCard, AppPage } from '../../components/general';
import { EmailType } from '../../models/emails/emailType';
import { useParams } from 'react-router-dom';
import { Email } from '../../models/emails/email';
import LockRequestEmail from '../../emails/LockRequestEmail';
import EmailTemplateEditor from '../../components/email/EmailTemplateEditor';
import { getEmailTemplateByType, upsertEmailTemplate } from '../../services/emailTemplateService';
import { EmailTemplateDto, EmailTemplateType } from '../../models/email-template-dto';
import { FormInputText, FormContainer } from '../../components/form';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { shortTextField } from '../../utils/validation-helper';

interface EmailTemplateForm {
  subject: string;
  toAddresses: string;
  ccAddresses: string;
  bccAddresses: string;
  replyToAddress: string;
  fromAddress: string;
}

const ViewEmailTemplate = () => {
  const errors = useRef<any>(null);
  const [email, setEmail] = useState<Email | null>(null);
  const [reactSourceCode, setReactSourceCode] = useState<string>('');
  const [htmlTemplate, setHtmlTemplate] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const { emailType } = useParams();

  const validationSchema = yup.object({
    subject: shortTextField('Subject').required('Subject is required'),
    fromAddress: shortTextField('From address').nullable(),
    toAddresses: shortTextField('To addresses').nullable(),
    ccAddresses: shortTextField('CC addresses').nullable(),
    bccAddresses: shortTextField('BCC addresses').nullable(),
    replyToAddress: shortTextField('Reply-to address').nullable(),
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors: formErrors },
  } = useForm<EmailTemplateForm>({
    resolver: yupResolver<any>(validationSchema),
    defaultValues: {
      subject: '',
      toAddresses: '',
      ccAddresses: '',
      bccAddresses: '',
      replyToAddress: '',
      fromAddress: '',
    },
  });

  const getEmail = useCallback((): Email | null => {
    switch (emailType) {
      case EmailType.LockRequestEmail:
        return {
          name: 'LockRequestEmail',
          displayName: 'Lock Request Email',
          description: 'Email sent when a lock request is made',
          type: EmailType.LockRequestEmail,
          templateFileName: 'LockRequestEmail.tsx',
        };
      default:
        return null;
    }
  }, [emailType]);

  const getEmailTemplateType = useCallback((): EmailTemplateType | null => {
    switch (emailType) {
      case EmailType.LockRequestEmail:
        return EmailTemplateType.LockRequest;
      default:
        return null;
    }
  }, [emailType]);

  const renderEmailPreview = async (email: Email | null): Promise<string> => {
    if (!email) {
      return '';
    }
    switch (email.type) {
      case EmailType.LockRequestEmail:
        return await render(<LockRequestEmail />);
      default:
        return '';
    }
  };

  const loadTemplate = async () => {
    const email = getEmail();
    const templateType = getEmailTemplateType();

    if (!email || templateType === null) {
      return;
    }

    setEmail(email);
    setLoading(true);

    try {
      const template = await getEmailTemplateByType(templateType);

      if (template) {
        setValue('subject', template.subject || '');
        setValue(
          'toAddresses',
          template.toAddresses?.map((a) => (a.alias ? `${a.alias} <${a.address}>` : a.address)).join(', ') || ''
        );
        setValue(
          'ccAddresses',
          template.ccAddresses?.map((a) => (a.alias ? `${a.alias} <${a.address}>` : a.address)).join(', ') || ''
        );
        setValue(
          'bccAddresses',
          template.bccAddresses?.map((a) => (a.alias ? `${a.alias} <${a.address}>` : a.address)).join(', ') || ''
        );
        setValue(
          'replyToAddress',
          template.replyToAddress
            ? template.replyToAddress.alias
              ? `${template.replyToAddress.alias} <${template.replyToAddress.address}>`
              : template.replyToAddress.address
            : ''
        );
        setValue(
          'fromAddress',
          template.fromAddress
            ? template.fromAddress.alias
              ? `${template.fromAddress.alias} <${template.fromAddress.address}>`
              : template.fromAddress.address
            : ''
        );
        setReactSourceCode(template.reactSourceCode || '');
        setHtmlTemplate(template.htmlTemplate || '');
      } else {
        const module = await import(`!!raw-loader!../../emails/${email.templateFileName}`);
        setReactSourceCode(module.default);
        const html = await renderEmailPreview(email);
        setHtmlTemplate(html);
      }
    } catch (error) {
      console.error('Error loading template:', error);
      errors.current?.show([{ severity: 'error', summary: 'Error', detail: 'Failed to load email template', sticky: true }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplate();
  }, [emailType]);

  const showError = (error: string) => {
    errors.current?.show([{ severity: 'error', summary: 'Error', detail: error, sticky: true }]);
  };

  const showSuccess = (message: string) => {
    errors.current?.show([{ severity: 'success', summary: 'Success', detail: message, life: 3000 }]);
  };

  const onSubmit = handleSubmit(async (data: EmailTemplateForm) => {
    const templateType = getEmailTemplateType();
    if (templateType === null) {
      return;
    }

    setLoading(true);

    try {
      const parseAddress = (addr: string) => {
        const match = addr.match(/^(?:(.+?)\s*<(.+?)>|(.+))$/);
        if (match) {
          return {
            alias: match[1] || undefined,
            address: match[2] || match[3],
          };
        }
        return { address: addr };
      };

      const dto: EmailTemplateDto = {
        emailTemplateType: templateType,
        subject: data.subject,
        toAddresses: data.toAddresses
          .split(',')
          .map((a) => a.trim())
          .filter((a) => a)
          .map(parseAddress),
        ccAddresses: data.ccAddresses
          .split(',')
          .map((a) => a.trim())
          .filter((a) => a)
          .map(parseAddress),
        bccAddresses: data.bccAddresses
          .split(',')
          .map((a) => a.trim())
          .filter((a) => a)
          .map(parseAddress),
        replyToAddress: data.replyToAddress ? parseAddress(data.replyToAddress) : undefined,
        fromAddress: data.fromAddress ? parseAddress(data.fromAddress) : undefined,
        htmlTemplate: htmlTemplate,
        reactSourceCode: reactSourceCode,
      };

      await upsertEmailTemplate(dto);
      showSuccess('Email template saved successfully');
    } catch (error) {
      console.error('Error saving template:', error);
      showError('Failed to save email template');
    } finally {
      setLoading(false);
    }
  });

  return (
    <AppPage title={'Email Template - ' + email?.displayName} breadcrumbs="Admin - Email Templates">
      <AppCard>
        <FormContainer loading={loading} errors={errors} onSubmit={onSubmit}>
          <div className="!grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <FormInputText
              control={control}
              field="subject"
              label="Subject"
              placeholder="Enter email subject"
              errors={formErrors}
              required
            />
            <FormInputText
              control={control}
              field="fromAddress"
              label="From Address"
              placeholder="Name <email@example.com>"
              errors={formErrors}
            />
          </div>

          <div className="!grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <FormInputText
              control={control}
              field="toAddresses"
              label="To Addresses"
              placeholder="email1@example.com, Name <email2@example.com>"
              errors={formErrors}
            />
            <FormInputText
              control={control}
              field="replyToAddress"
              label="Reply To Address"
              placeholder="Name <email@example.com>"
              errors={formErrors}
            />
          </div>

          <div className="!grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <FormInputText
              control={control}
              field="ccAddresses"
              label="CC Addresses"
              placeholder="email1@example.com, email2@example.com"
              errors={formErrors}
            />
            <FormInputText
              control={control}
              field="bccAddresses"
              label="BCC Addresses"
              placeholder="email1@example.com, email2@example.com"
              errors={formErrors}
            />
          </div>

          <div className="mt-4">
            <EmailTemplateEditor
              reactSourceCode={reactSourceCode}
              htmlTemplate={htmlTemplate}
              onReactCodeChange={setReactSourceCode}
              onHtmlChange={setHtmlTemplate}
            />
          </div>
        </FormContainer>
      </AppCard>
    </AppPage>
  );
};

export default ViewEmailTemplate;
